import React, { useCallback, useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { toast } from "react-toastify"

import _ from "lodash"
import { getCalculatorQuestions } from "../../../requesters/CalculatorRequester"

import { getCalculatorResult, updateCalculatorResult } from "../../../requesters/CalculatorResultsRequester"
import { sendLearnWorldAccessRequest } from "../../../requesters/UsersRequester"
import FormSection from "../Matchfit/FormSection"
import GeneralInfoSection from "../Matchfit/GeneralInfoSection"
import LiveLifeHeader from "./LiveLifeHeader"
import {
  HeartRateFormSection, MovementFormSection, RecoveryFormSection, SittingTimeFormSection
} from "../Matchfit/SpecificFormSections"
import { liveLifeDashboardPath } from "../../Dashboard/LiveLife/constants"
import { apiErrorMessage } from "../../../util"
import { FLOAT_FIELD_QUESTION_SLUGS } from "../constants"

function LiveLifeFormPage({ baseUrl, currentUser }) {
  const location = useLocation()
  const [questionSets, setQuestionSets] = useState()
  const [form, setForm] = useState({})
  const [currentSection, setCurrentSection] = useState(100)
  const [totalQuestionLength, setTotalQuestionLength] = useState(0)
  const [lastPage, setLastPage] = useState(6)
  const [resultId, setResultId] = useState("")
  const [calculatorVersion, setCalculatorVersion] = useState("1")
  const { moduleId } = useParams()

  // Section retake (initiated from the PQ Index dashboard): scope the form to a
  // single section and answer it against the pre-populated draft created by
  // POST /calculator_results/section_retake — no pre-fill of carried-over answers.
  const retakeParams = new URLSearchParams(location?.search || "")
  const retakeResultId = retakeParams.get("retake_result_id")
  const retakeSection = retakeParams.get("section")
  // Only enter retake mode with a complete link — both the draft id and the
  // section. A partial link falls back to the normal full-survey flow.
  const isRetake = !!(retakeResultId && retakeSection)

  useEffect(() => {
    document.title = `Performance Intelligence Academy | ${String(calculatorVersion) === "2" ? "PQ Index" : "LiveLife Score"} Questions`
  }, [calculatorVersion])

  const fetchCalculatorResult = useCallback(questions => {
    getCalculatorResult(baseUrl, currentUser, "live_life", currentUser.active_live_life_id).then(res => {
      setCurrentSection(0)
      const {
        calc_responses: calculatorResponses,
        calc_responses_by_sorting_index: calculatorResponsesBySortingIndex,
      } = res.response.json?.calculator_results || {}
      if (calculatorResponses?.length) {
        const retrievedAnswers = {}
        calculatorResponses.forEach(cr => {
          retrievedAnswers[cr.question_slug] = {
            answer_id: cr.answer_id,
            answer_slug: cr.answer_slug,
            free_text_field: cr.free_text_field,
            number_field: cr.float_field || cr.number_field,
            hours: cr.hours,
            minutes: cr.minutes,
            seconds: cr.seconds,
          }
        })
        setForm(retrievedAnswers)

        if (calculatorResponsesBySortingIndex.length) {
          const lastAnswer = _.last(calculatorResponsesBySortingIndex)
          const slug = lastAnswer?.question_slug
          const lastQuestion = questions.find(q => q.questions.map(item => item.question_slug).includes(slug) || q.input_question_slugs.includes(slug))
          const currentSection = lastQuestion?.page_index
          setCurrentSection(currentSection)
        }
      }
      setResultId(res.response.json?.calculator_results?.id)
    })
  }, [baseUrl, currentUser])

  const fetchQuestionSet = useCallback(() => {
    const params = { id: currentUser.active_live_life_id, calculator_type: "live_life", module_id: moduleId }
    if (isRetake) params.section = retakeSection
    getCalculatorQuestions(baseUrl, params, currentUser).then(res => {
      setQuestionSets(res.response.grouped_questions)
      setTotalQuestionLength(res.response.question_length)
      setLastPage(res.response.last_page)
      setCalculatorVersion(res.response.version || "1")
      if (isRetake) {
        // Answer the scoped section against the existing pre-populated draft,
        // starting from a blank form on the first page. Reset form state in case
        // the component instance is reused (React Router keeps it across query
        // string changes), so stale answers from a prior session can't leak into
        // the retake draft and overwrite carried-over responses.
        setForm({})
        setResultId(retakeResultId)
        setCurrentSection(0)
      } else {
        fetchCalculatorResult(res.response.grouped_questions)
      }
    })
  }, [baseUrl, currentUser, moduleId, fetchCalculatorResult, isRetake, retakeSection, retakeResultId])

  useEffect(() => {
    if (!currentUser?.active_live_life_id) {
      window.location.href = "/overview"
    } else {
      fetchQuestionSet()
    }
  }, [currentUser?.active_live_life_id, fetchQuestionSet])

  const onChangeHandler = e => {
    const { name, id } = e.target
    const formCopy = { ...form }
    const answer_slug = e.target.getAttribute("data-slug")
    formCopy[name] = { answer_id: id, answer_slug }

    // TODO: wipe appropriate fields when user selects "No"
    if (answer_slug === "tobacco_yes_no_1") {
      delete formCopy.tobacco_cigarettes_quantity
      delete formCopy.tobacco_cigars_quantity
      delete formCopy.tobacco_pipes_quantity
      delete formCopy.tobacco_vapes_quantity
    } else if (answer_slug === "wears_activity_monitor_1") {
      delete formCopy.monitor_tracks_minutes
      delete formCopy.monitor_tracks_heartbeat
      delete formCopy.vo2_yes_no
      delete formCopy.vo2
    }

    setForm(formCopy)
  }

  const onFreeTextChangeHandler = e => {
    const { id, name, value } = e.target
    const formCopy = { ...form }
    formCopy[name] = { answer_id: id, free_text_field: value }
    setForm(formCopy)
  }

  const onInputChangeHandler = e => {
    const {
      name, id, value, hours, minutes, seconds, min, max
    } = e.target
    // if value is not within the range, set it to the min or max
    let errors = []
    if (min && max) {
      if (Number(value) > Number(max)) {
        errors = [`Please enter a ${name} value less than ${max}`]
      } else if (Number(value) < Number(min)) {
        errors = [`Please enter a ${name} value greater than ${min}`]
      }
      // if within the range, remove the error
      if (Number(value) <= Number(max) && Number(value) >= Number(min)) {
        errors = []
      }
    }

    const formCopy = { ...form }
    formCopy[name] = {
      answer_id: id,
      // Keep the raw input string (matches Matchfit's handler). Prevent preceding 0
      number_field: value === "" ? "" : Number(value),
      hours,
      minutes,
      seconds,
      errors,
    }
    setForm(formCopy)
  }

  // LearnWorlds programs send the user to the LearnWorlds homepage once they've
  // completed LiveLife; everyone else lands on the portal LiveLife dashboard.
  const redirectToLearnWorld = () => {
    sendLearnWorldAccessRequest(baseUrl, currentUser.program_id)
      .then(res => {
        if (res.response?.status === "ok") {
          window.location.href = res.response.json.learn_world.url
        } else {
          window.location.href = liveLifeDashboardPath(calculatorVersion)
        }
        return res
      })
      .catch(() => {
        window.location.href = liveLifeDashboardPath(calculatorVersion)
      })
  }

  const onSaveHandler = e => {
    // Submit only from the button Done; "Save Draft" persists progress without submitting.
    const finalSubmit = e.target.name !== "saveDraft"

    const isV2 = String(calculatorVersion) === "2"
    const isLearnWorld = currentUser.active_enrolment?.integration_type === "learn_world"

    FLOAT_FIELD_QUESTION_SLUGS.forEach(slug => {
      const answer = form[slug]
      if (answer?.number_field) {
        answer.float_field = answer.number_field
        delete answer.number_field
      }
    })

    const payload = _.toArray(form)
    return updateCalculatorResult(baseUrl, resultId, payload, finalSubmit, currentUser, moduleId, currentSection, calculatorVersion).then(res => {
      if (res.response?.status !== "ok") {
        return
      }

      // PQ Index (v2): completion always lands the participant on their results
      // summary dashboard. The LearnWorlds course is reachable from there — the
      // calculator must never redirect straight into the course.
      if (finalSubmit && isV2) {
        window.location.href = liveLifeDashboardPath(calculatorVersion)
        return
      }

      // Non-PQ-Index (v1) LearnWorlds completions still redirect this tab out to LearnWorlds;
      // draft saves and other programs keep going to the dashboard.
      if (finalSubmit && isLearnWorld) {
        redirectToLearnWorld()
        return
      }
      window.location.href = liveLifeDashboardPath(calculatorVersion)
    }).catch(err => {
      toast.error(`Error: ${apiErrorMessage(err)}`)
    })
  }

  return (
    <div className="survey-sticky-footer">
      <div className="container survey-sticky-footer__content">
        <LiveLifeHeader onSaveHandler={onSaveHandler} currentSection={currentSection} lastPage={lastPage} calculatorVersion={calculatorVersion} />
        {/* Loading effect when formpage loading */}
        {currentSection === 100 && (
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}
        {!!questionSets && questionSets.map(questionSet => {
        if (questionSet.section_slug === "general_information") {
          return (
            <GeneralInfoSection
              calculatorName={String(calculatorVersion) === "2" ? "PQ Index" : "LiveLife Score"}
              currentSection={currentSection}
              form={form}
              groupName={questionSet.group_name}
              key={questionSet.page_index}
              lastPage={lastPage}
              onChangeHandler={onFreeTextChangeHandler}
              onSubmit={onSaveHandler}
              questionSet={questionSet}
              setCurrentSection={setCurrentSection}
              title={questionSet.section_title}
              totalQuestionLength={totalQuestionLength} />
          )
        }
        if (questionSet.section_slug === "sitting") {
          return (
            <SittingTimeFormSection
              currentSection={currentSection}
              form={form}
              key={questionSet.page_index}
              groupName={questionSet.group_name}
              onInputChangeHandler={onInputChangeHandler}
              questionSet={questionSet}
              setCurrentSection={setCurrentSection}
              title={questionSet.section_title} />
          )
        }
        if (questionSet.section_slug === "activity_monitor") {
          return (
            <FormSection
              currentSection={currentSection}
              form={form}
              key={questionSet.page_index}
              groupName={questionSet.group_name}
              isLivelife
              lastPage={lastPage}
              onChangeHandler={onChangeHandler}
              onInputChangeHandler={onInputChangeHandler}
              onSubmit={onSaveHandler}
              questionSet={questionSet}
              setCurrentSection={setCurrentSection}
              title={questionSet.section_title}
              totalQuestionLength={totalQuestionLength} />
          )
        }
        if (questionSet.section_slug === "heart_rate") {
          return (
            <HeartRateFormSection
              currentSection={currentSection}
              form={form}
              key={questionSet.page_index}
              groupName={questionSet.group_name}
              lastPage={lastPage}
              onChangeHandler={onChangeHandler}
              onInputChangeHandler={onInputChangeHandler}
              onSubmit={onSaveHandler}
              questionSet={questionSet}
              setCurrentSection={setCurrentSection}
              title={questionSet.section_title}
              totalQuestionLength={totalQuestionLength} />
          )
        }
        if (questionSet.section_slug === "movement") {
          return (
            <MovementFormSection
              currentSection={currentSection}
              form={form}
              key={questionSet.page_index}
              groupName={questionSet.group_name}
              lastPage={lastPage}
              onChangeHandler={onChangeHandler}
              onInputChangeHandler={onInputChangeHandler}
              onSubmit={onSaveHandler}
              questionSet={questionSet}
              setCurrentSection={setCurrentSection}
              title={questionSet.section_title}
              totalQuestionLength={totalQuestionLength} />
          )
        }
        if (questionSet.section_slug === "switching_off") {
          return (
            <RecoveryFormSection
              currentSection={currentSection}
              form={form}
              key={questionSet.page_index}
              groupName={questionSet.group_name}
              lastPage={lastPage}
              onChangeHandler={onChangeHandler}
              onInputChangeHandler={onInputChangeHandler}
              onSubmit={onSaveHandler}
              questionSet={questionSet}
              setCurrentSection={setCurrentSection}
              title={questionSet.section_title}
              totalQuestionLength={totalQuestionLength} />
          )
        }
        return (
          <FormSection
            currentSection={currentSection}
            form={form}
            key={questionSet.page_index}
            groupName={questionSet.group_name}
            isLivelife
            lastPage={lastPage}
            onChangeHandler={onChangeHandler}
            onInputChangeHandler={onInputChangeHandler}
            onSubmit={onSaveHandler}
            questionSet={questionSet}
            setCurrentSection={setCurrentSection}
            title={questionSet.section_title}
            description={questionSet.section_description}
            totalQuestionLength={totalQuestionLength} />
        )
      })}
      </div>
    </div>
  )
}

export default LiveLifeFormPage
